/**
 * Drawing. Planck simulates and says nothing about how anything looks, so
 * every mark on the canvas is made here, from the body list, once a frame.
 *
 * The paper is the point. The graph paper is printed, so its rules are dead
 * straight; everything on top of it is drawn by hand, so every outline is a
 * polyline with a little wobble in it, inked twice the way a pen goes round
 * a shape twice, with the fill sitting slightly off the line the way colour
 * never quite lands inside the ink. Anything that moves has its wobble
 * re-rolled five times a second, so it boils the way a hand-drawn animation
 * boils; the yard itself was drawn once and stays put. Under it all is a
 * sheet of grain.
 *
 * The paper is drawn in world space, so it scrolls with the tank instead of
 * sitting on the screen like a texture.
 */

import type { Body, World } from "planck";
import { colours, entityOf, type Entity } from "./world.ts";
import type { Particles } from "./particles.ts";
import { TILE } from "./levels.ts";

export type Camera = {
  x: number;
  y: number;
  /** Pixels per metre. */
  scale: number;
  /** Radians the world is turned by on screen. Zero is north up. */
  rot: number;
  shakeX: number;
  shakeY: number;
};

export type View = { width: number; height: number };

/**
 * How many metres the short side of the screen shows.
 *
 * A phone gets *fewer*, not more: the screen is smaller, so the same number of
 * metres would put the tank at a few dozen pixels and the game would be played
 * by squinting. A desk gets a wider view because it can afford one.
 */
export const viewMetres = (view: View): number => (Math.min(view.width, view.height) < 560 ? 19 : 28);

export const cameraScale = (view: View): number =>
  Math.min(view.width, view.height) / viewMetres(view);

export const screenToWorld = (cam: Camera, view: View, px: number, py: number) => {
  const dx = (px - view.width / 2) / cam.scale;
  const dy = (py - view.height / 2) / cam.scale;
  const cs = Math.cos(-cam.rot);
  const sn = Math.sin(-cam.rot);
  return { x: cam.x + dx * cs - dy * sn, y: cam.y + dx * sn + dy * cs };
};

export const worldToScreen = (cam: Camera, view: View, wx: number, wy: number) => {
  const dx = wx - cam.x;
  const dy = wy - cam.y;
  const cs = Math.cos(cam.rot);
  const sn = Math.sin(cam.rot);
  return {
    x: view.width / 2 + (dx * cs - dy * sn) * cam.scale,
    y: view.height / 2 + (dx * sn + dy * cs) * cam.scale,
  };
};

export type Scene = {
  world: World;
  cam: Camera;
  particles: Particles;
  view: View;
  /** Seconds since the level began, for anything that pulses. */
  time: number;
  /** Things worth an arrow at the edge of the screen when they are off it. */
  markers: Array<{ x: number; y: number; colour: string }>;
  reduced: boolean;
  /** Ink only: no colour, hatching where the colour would have been. */
  mono: boolean;
  /** Where the gun is being pointed, in screen pixels, and whether it is
   *  loaded. Null when nothing is aiming by hand. */
  aim: { x: number; y: number; ready: boolean } | null;
  /** Road tiles, with which sides join on to more road. */
  roads: Array<{ x: number; y: number; e: boolean; w: boolean; n: boolean; s: boolean }>;
};

export function render(c: CanvasRenderingContext2D, dpr: number, scene: Scene): void {
  const { view, cam } = scene;
  frameBucket = scene.reduced ? 0 : Math.floor(scene.time * 5);
  bucket = 0;
  mono = scene.mono;

  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  c.fillStyle = colours.cream;
  c.fillRect(0, 0, view.width, view.height);

  c.save();
  c.translate(view.width / 2 + cam.shakeX, view.height / 2 + cam.shakeY);
  c.scale(cam.scale, cam.scale);
  c.rotate(cam.rot);
  c.translate(-cam.x, -cam.y);
  c.lineJoin = "round";
  c.lineCap = "round";

  // With the world turned, the corners of the screen reach further than
  // half the width: use the diagonal so the paper never runs out.
  const reach = Math.hypot(view.width, view.height) / 2 / cam.scale + 2;
  const bounds = { x0: cam.x - reach, x1: cam.x + reach, y0: cam.y - reach, y1: cam.y + reach };

  paper(c, bounds, cam.scale);
  roads(c, scene);
  treads(c, scene);

  // Two passes so nothing living is hidden under rubble.
  const flat: Array<[Body, Entity]> = [];
  const tall: Array<[Body, Entity]> = [];
  for (let b = scene.world.getBodyList(); b; b = b.getNext()) {
    const e = entityOf(b);
    if (!e || e.dead) continue;
    const p = b.getPosition();
    const r = (e.r ?? Math.hypot(e.hw ?? 1, e.hh ?? 1)) + 1;
    if (p.x + r < bounds.x0 || p.x - r > bounds.x1 || p.y + r < bounds.y0 || p.y - r > bounds.y1) continue;
    (LOW.has(e.kind) ? flat : tall).push([b, e]);
  }
  for (const [b, e] of flat) paintBody(c, b, e, scene);
  for (const [b, e] of tall) paintBody(c, b, e, scene);
  bucket = frameBucket;

  worldParticles(c, scene);
  c.restore();

  grain(c, view);
  screenParticles(c, scene);
  edgeMarkers(c, scene);
  if (scene.aim) reticle(c, scene.aim);
}

/**
 * The gunsight. Drawn rather than asked of the operating system, which drops
 * a custom cursor now and then over a canvas this busy. Screen pixels, so it
 * is the same size however far the camera is pulled back, and it says
 * whether there is a round ready: a filled centre means fire.
 */
function reticle(c: CanvasRenderingContext2D, aim: { x: number; y: number; ready: boolean }): void {
  const r = 13;
  c.save();
  c.translate(Math.round(aim.x), Math.round(aim.y));
  c.lineCap = "round";
  // Cream first and wider, so the ink on top reads on pale paper and on a
  // dark hull alike.
  for (const halo of [true, false]) {
    c.strokeStyle = halo ? "rgba(255,255,240,0.92)" : colours.ink;
    c.lineWidth = halo ? 5.5 : 2.4;
    c.beginPath();
    c.arc(0, 0, r, 0, Math.PI * 2);
    for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]] as const) {
      c.moveTo(dx * (r - 4.5), dy * (r - 4.5));
      c.lineTo(dx * (r + 5.5), dy * (r + 5.5));
    }
    c.stroke();
  }
  c.beginPath();
  c.arc(0, 0, 2.4, 0, Math.PI * 2);
  if (aim.ready) {
    c.fillStyle = mono ? colours.ink : colours.acid;
    c.fill();
  }
  c.strokeStyle = colours.ink;
  c.lineWidth = 1.6;
  c.stroke();
  c.restore();
}

const LOW = new Set(["exit", "pickup", "debris", "wreck"]);

// ---- the hand ----------------------------------------------------------------

type Pt = { x: number; y: number };

/** Which roll of the dice this frame is drawn with. */
let bucket = 0;
let mono = false;

/** How far a point strays from where it should be, in metres. */
const WOBBLE = 0.038;

/**
 * Only things that move get to boil. The yard is paper someone already drew:
 * its wobble is fixed the day it was made, and a wall that twitches is a wall
 * you cannot aim at. Tanks, turrets and shells are the animation.
 */
const LIVE = new Set(["player", "enemy", "boss", "turret", "shell"]);
let frameBucket = 0;

/** Every entity gets a number once, so its wobble is its own and stays put. */
const seeds = new WeakMap<object, number>();
let nextSeed = 1;
const seedOf = (o: object): number => {
  let s = seeds.get(o);
  if (!s) {
    s = nextSeed++;
    seeds.set(o, s);
  }
  return s;
};

/** A repeatable -1…1 for a point on a shape, this roll. */
const jitter = (seed: number, i: number): number => {
  const x = Math.sin(seed * 12.9898 + i * 78.233 + bucket * 37.719) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
};

/** Put the pen down and go round the points, missing each by a hair. */
function sketch(c: CanvasRenderingContext2D, pts: Pt[], seed: number, closed = true): void {
  c.beginPath();
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i] as Pt;
    const x = p.x + jitter(seed, i * 2) * WOBBLE;
    const y = p.y + jitter(seed, i * 2 + 1) * WOBBLE;
    if (i === 0) c.moveTo(x, y);
    else c.lineTo(x, y);
  }
  if (closed) c.closePath();
}

/** A straight run split into steps, so a long edge can wander too. */
function along(out: Pt[], a: Pt, b: Pt, step = 0.6): void {
  const n = Math.max(1, Math.round(Math.hypot(b.x - a.x, b.y - a.y) / step));
  for (let i = 0; i < n; i++) {
    out.push({ x: a.x + ((b.x - a.x) * i) / n, y: a.y + ((b.y - a.y) * i) / n });
  }
}

function rectPts(x: number, y: number, w: number, h: number, r: number): Pt[] {
  const rr = Math.min(r, w / 2, h / 2);
  const out: Pt[] = [];
  const corner = (cx: number, cy: number, from: number) => {
    for (let i = 0; i <= 3; i++) {
      const a = from + (i / 3) * (Math.PI / 2);
      out.push({ x: cx + Math.cos(a) * rr, y: cy + Math.sin(a) * rr });
    }
  };
  along(out, { x: x + rr, y }, { x: x + w - rr, y });
  corner(x + w - rr, y + rr, -Math.PI / 2);
  along(out, { x: x + w, y: y + rr }, { x: x + w, y: y + h - rr });
  corner(x + w - rr, y + h - rr, 0);
  along(out, { x: x + w - rr, y: y + h }, { x: x + rr, y: y + h });
  corner(x + rr, y + h - rr, Math.PI / 2);
  along(out, { x, y: y + h - rr }, { x, y: y + rr });
  corner(x + rr, y + rr, Math.PI);
  return out;
}

function circlePts(r: number, cx = 0, cy = 0): Pt[] {
  const n = Math.max(10, Math.round(r * 22));
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    out.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  return out;
}

/**
 * Colour in, then ink round. The fill is nudged a touch so it never sits
 * exactly inside the line, and the ink goes round twice: once properly, once
 * lighter and a little off, the way a pen retraces.
 */
function draw(
  c: CanvasRenderingContext2D,
  pts: Pt[],
  seed: number,
  fill: string | CanvasPattern | null,
  lw: number,
  stroke: string = colours.ink,
): void {
  if (fill) {
    c.save();
    c.translate(0.025, 0.03);
    sketch(c, pts, seed + 3);
    c.fillStyle = fill;
    c.fill();
    c.restore();
  }
  if (lw <= 0) return;
  sketch(c, pts, seed);
  c.strokeStyle = stroke;
  c.lineWidth = lw;
  c.stroke();
  const alpha = c.globalAlpha;
  c.globalAlpha = alpha * 0.4;
  sketch(c, pts, seed + 11);
  c.lineWidth = lw * 0.55;
  c.stroke();
  c.globalAlpha = alpha;
}

/** A single stroke of the pen from a to b, wobbled. */
function line(c: CanvasRenderingContext2D, seed: number, a: Pt, b: Pt, lw: number, stroke: string): void {
  const pts: Pt[] = [];
  along(pts, a, b, 0.5);
  pts.push(b);
  sketch(c, pts, seed, false);
  c.strokeStyle = stroke;
  c.lineWidth = lw;
  c.stroke();
}

// ---- ink only -----------------------------------------------------------------

let hatchPattern: CanvasPattern | null = null;
let hatchScale = 0;

/** Diagonal pen strokes, for anything that was a colour. */
function hatch(c: CanvasRenderingContext2D, scale: number): CanvasPattern | null {
  if (hatchPattern && hatchScale === scale) return hatchPattern;
  const tile = document.createElement("canvas");
  tile.width = 16;
  tile.height = 16;
  const t = tile.getContext("2d");
  if (!t) return null;
  t.strokeStyle = colours.ink;
  t.lineWidth = 2.2;
  t.lineCap = "round";
  t.beginPath();
  t.moveTo(-4, 20);
  t.lineTo(20, -4);
  t.moveTo(-4, 4);
  t.lineTo(4, -4);
  t.moveTo(12, 20);
  t.lineTo(20, 12);
  t.stroke();
  const pat = c.createPattern(tile, "repeat");
  if (pat) {
    // Pattern space is world space here: 16 tile pixels to a quarter metre.
    pat.setTransform(new DOMMatrix().scale(0.25 / 16));
  }
  hatchPattern = pat;
  hatchScale = scale;
  return pat;
}

/** The colour a thing is drawn in, given how many pencils are left. */
function paint(c: CanvasRenderingContext2D, e: Entity, fill: string, scale: number): string | CanvasPattern | null {
  if (!mono) return fill;
  if (fill === colours.ink) return colours.ink;
  const inked = e.kind === "enemy" || e.kind === "turret" || e.kind === "boss" || e.kind === "barrel" || e.kind === "shell";
  if (inked && fill !== colours.acid && fill !== colours.cream) return hatch(c, scale);
  return colours.cream;
}

// ---- the paper -------------------------------------------------------------

function paper(
  c: CanvasRenderingContext2D,
  b: { x0: number; x1: number; y0: number; y1: number },
  scale: number,
): void {
  const fine = 1;
  const bold = TILE * 4;
  c.lineWidth = 1 / scale;
  c.strokeStyle = "#f0f0dd";
  c.beginPath();
  for (let x = Math.floor(b.x0 / fine) * fine; x < b.x1; x += fine) {
    c.moveTo(x, b.y0);
    c.lineTo(x, b.y1);
  }
  for (let y = Math.floor(b.y0 / fine) * fine; y < b.y1; y += fine) {
    c.moveTo(b.x0, y);
    c.lineTo(b.x1, y);
  }
  c.stroke();

  c.lineWidth = 1.5 / scale;
  c.strokeStyle = "#e6e6d0";
  c.beginPath();
  for (let x = Math.floor(b.x0 / bold) * bold; x < b.x1; x += bold) {
    c.moveTo(x, b.y0);
    c.lineTo(x, b.y1);
  }
  for (let y = Math.floor(b.y0 / bold) * bold; y < b.y1; y += bold) {
    c.moveTo(b.x0, y);
    c.lineTo(b.x1, y);
  }
  c.stroke();
}

/** Roads: a grey strip with a dashed centre line, drawn once per tile. */
function roads(c: CanvasRenderingContext2D, scene: Scene): void {
  if (!scene.roads.length) return;
  const half = TILE / 2;
  c.fillStyle = mono ? "rgba(25,25,25,0.08)" : "#dcdccb";
  for (const r of scene.roads) c.fillRect(r.x - half, r.y - half, TILE, TILE);
  c.strokeStyle = mono ? "rgba(25,25,25,0.4)" : "rgba(255,255,240,0.9)";
  c.lineWidth = 0.12;
  c.setLineDash([0.5, 0.4]);
  // The centre line goes between the two lanes, not down the middle of each:
  // a tile draws it on the shared edge only when it is the near lane of a
  // two-lane street. Where the road runs both ways it is a junction, and a
  // junction is left unmarked.
  c.beginPath();
  for (const r of scene.roads) {
    const across = r.e || r.w;
    const along = r.n || r.s;
    if (across && r.s && !r.n) {
      c.moveTo(r.x - half, r.y + half);
      c.lineTo(r.x + half, r.y + half);
    }
    if (along && r.e && !r.w) {
      c.moveTo(r.x + half, r.y - half);
      c.lineTo(r.x + half, r.y + half);
    }
  }
  c.stroke();
  // Kerbs along the open sides.
  c.strokeStyle = mono ? "rgba(25,25,25,0.5)" : "rgba(25,25,25,0.22)";
  c.lineWidth = 0.08;
  c.beginPath();
  for (const r of scene.roads) {
    if (!r.n) {
      c.moveTo(r.x - half, r.y - half);
      c.lineTo(r.x + half, r.y - half);
    }
    if (!r.s) {
      c.moveTo(r.x - half, r.y + half);
      c.lineTo(r.x + half, r.y + half);
    }
    if (!r.w) {
      c.moveTo(r.x - half, r.y - half);
      c.lineTo(r.x - half, r.y + half);
    }
    if (!r.e) {
      c.moveTo(r.x + half, r.y - half);
      c.lineTo(r.x + half, r.y + half);
    }
  }
  c.stroke();
  c.setLineDash([]);
}

let grainPattern: CanvasPattern | null = null;

/** Specks, so the sheet reads as paper and not as a colour. Screen space. */
function grain(c: CanvasRenderingContext2D, view: View): void {
  if (!grainPattern) {
    const tile = document.createElement("canvas");
    tile.width = 160;
    tile.height = 160;
    const t = tile.getContext("2d");
    if (!t) return;
    for (let i = 0; i < 900; i++) {
      const a = 0.03 + Math.random() * 0.06;
      t.fillStyle = Math.random() < 0.8 ? `rgba(25,25,25,${a})` : `rgba(255,210,18,${a * 1.6})`;
      const s = Math.random() < 0.85 ? 1 : 2;
      t.fillRect(Math.random() * 160, Math.random() * 160, s, s);
    }
    grainPattern = c.createPattern(tile, "repeat");
  }
  if (!grainPattern) return;
  c.save();
  c.fillStyle = grainPattern;
  c.fillRect(0, 0, view.width, view.height);
  c.restore();
}

function treads(c: CanvasRenderingContext2D, scene: Scene): void {
  c.save();
  for (const p of scene.particles.all) {
    if (!p.live || p.kind !== "tread") continue;
    const fade = 1 - p.age / p.life;
    c.globalAlpha = fade * 0.16;
    c.fillStyle = p.colour;
    c.save();
    c.translate(p.x, p.y);
    c.rotate(p.angle);
    c.fillRect(-p.size, -0.16, p.size * 2, 0.32);
    c.restore();
  }
  c.restore();
}

// ---- bodies ----------------------------------------------------------------

function paintBody(c: CanvasRenderingContext2D, body: Body, e: Entity, scene: Scene): void {
  const p = body.getPosition();
  const lw = e.lw / scene.cam.scale;
  const seed = seedOf(e);
  bucket = LIVE.has(e.kind) ? frameBucket : 0;
  c.save();
  c.translate(p.x, p.y);

  switch (e.kind) {
    case "player":
    case "enemy":
    case "boss":
      c.rotate(body.getAngle());
      tank(c, e, lw, body.getAngle(), seed, scene);
      break;
    case "turret":
      turretPost(c, e, lw, seed, scene);
      break;
    case "shell": {
      const v = body.getLinearVelocity();
      if (v.lengthSquared() > 0.01) c.rotate(Math.atan2(v.y, v.x));
      shell(c, e, lw, seed, scene);
      break;
    }
    case "pickup":
      pickup(c, e, lw, seed, scene);
      break;
    case "exit":
      exitPad(c, e, lw, seed, scene);
      break;
    case "barrel":
      barrel(c, e, lw, seed, scene);
      break;
    case "wreck":
      c.rotate(body.getAngle());
      wreck(c, e, lw, seed);
      break;
    case "factory":
      factory(c, e, lw, seed, scene);
      break;
    default:
      c.rotate(body.getAngle());
      plate(c, e, lw, seed, scene);
      break;
  }

  c.restore();
}

const flashed = (e: Entity): string | null =>
  e.flash && e.flash > 0 ? (e.flash > 0.06 ? colours.acid : colours.cream) : null;

const faint = (alpha: number) => (mono ? `rgba(25,25,25,${alpha})` : `rgba(25,25,25,${alpha})`);

/** A tile, a crate, a car, a chunk of debris: anything that is just a box. */
function plate(c: CanvasRenderingContext2D, e: Entity, lw: number, seed: number, scene: Scene): void {
  const hw = e.hw ?? 0.5;
  const hh = e.hh ?? 0.5;
  const fill = paint(c, e, flashed(e) ?? e.fill, scene.cam.scale);
  draw(c, rectPts(-hw, -hh, hw * 2, hh * 2, Math.min(0.22, hw * 0.4)), seed, fill, lw);

  if (e.kind === "tile" && e.ch === "=") {
    // Brick courses, so a wall reads as masonry rather than a yellow slab.
    c.save();
    c.beginPath();
    c.rect(-hw, -hh, hw * 2, hh * 2);
    c.clip();
    const ink = faint(0.3);
    const w = lw * 0.5;
    for (let i = 1; i < 3; i++) {
      const y = -hh + (hh * 2 * i) / 3;
      line(c, seed + 20 + i, { x: -hw, y }, { x: hw, y }, w, ink);
    }
    line(c, seed + 30, { x: 0, y: -hh }, { x: 0, y: -hh + (hh * 2) / 3 }, w, ink);
    line(c, seed + 31, { x: -hw / 2, y: -hh + (hh * 2) / 3 }, { x: -hw / 2, y: -hh + (hh * 4) / 3 }, w, ink);
    line(c, seed + 32, { x: hw / 2, y: -hh + (hh * 2) / 3 }, { x: hw / 2, y: -hh + (hh * 4) / 3 }, w, ink);
    line(c, seed + 33, { x: 0, y: -hh + (hh * 4) / 3 }, { x: 0, y: hh }, w, ink);
    c.restore();
  }

  if (e.kind === "tile" && e.ch === "-") {
    const ink = mono ? faint(0.35) : "rgba(255,255,240,0.55)";
    for (let i = -2; i <= 2; i++) {
      line(c, seed + 40 + i, { x: -hw + 0.1, y: (i * hh) / 2.4 }, { x: hw - 0.1, y: (i * hh) / 2.4 - 0.18 }, lw * 0.6, ink);
    }
  }

  if (e.kind === "prop" && (e.ch === "v" || e.ch === "V")) {
    // A car: a roof panel and lamps at both ends, so it reads from above.
    draw(c, rectPts(-hw * 0.55, -hh * 0.42, hw * 1.1, hh * 0.84, 0.12), seed + 50, mono ? colours.cream : "rgba(25,25,25,0.75)", lw * 0.5);
    // The alarm: every lamp blinks amber while it goes off.
    const blinking = (e.alarm ?? 0) > 0 && Math.floor((e.alarm ?? 0) * 4) % 2 === 0;
    const lamp = mono ? colours.ink : blinking ? "#ffa726" : colours.cream;
    const tail = mono ? colours.ink : blinking ? "#ffa726" : "#7a2a22";
    c.fillStyle = lamp;
    c.fillRect(hw - 0.16, -hh + 0.12, 0.14, 0.26);
    c.fillRect(hw - 0.16, hh - 0.38, 0.14, 0.26);
    c.fillStyle = tail;
    c.fillRect(-hw + 0.02, -hh + 0.12, 0.14, 0.26);
    c.fillRect(-hw + 0.02, hh - 0.38, 0.14, 0.26);
    if (blinking && !mono) {
      c.fillStyle = "rgba(255,167,38,0.25)";
      for (const [x, y] of [[hw, -hh + 0.25], [hw, hh - 0.25], [-hw, -hh + 0.25], [-hw, hh - 0.25]]) {
        c.beginPath();
        c.arc(x as number, y as number, 0.45, 0, Math.PI * 2);
        c.fill();
      }
    }
    if (e.route) {
      // A windscreen on the driven ones, so you can tell which way they face.
      c.fillStyle = mono ? colours.cream : "rgba(255,255,240,0.55)";
      c.fillRect(hw * 0.35, -hh * 0.55, hw * 0.22, hh * 1.1);
    }
    wear(c, e, hw, hh, lw, seed);
  }

  if (e.kind === "prop" && e.ch === "c") {
    const ink = faint(0.5);
    line(c, seed + 60, { x: -hw, y: -hh }, { x: hw, y: hh }, lw * 0.55, ink);
    line(c, seed + 61, { x: hw, y: -hh }, { x: -hw, y: hh }, lw * 0.55, ink);
  }

  // Concrete cracks as it loses health: the damage is the drawing.
  if (e.kind === "tile" && e.maxHp > 1 && e.hp < e.maxHp) {
    const gone = e.maxHp - e.hp;
    for (let i = 0; i < gone; i++) {
      const a = (i * 2.1 + 0.6) % (Math.PI * 2);
      line(c, seed + 70 + i, { x: 0, y: 0 }, { x: Math.cos(a) * hw * 1.5, y: Math.sin(a) * hh * 1.5 }, lw * 0.6, faint(0.55));
      line(
        c,
        seed + 80 + i,
        { x: Math.cos(a) * hw * 0.6, y: Math.sin(a) * hh * 0.6 },
        { x: Math.cos(a + 1.9) * hw * 1.3, y: Math.sin(a + 1.9) * hh * 1.3 },
        lw * 0.6,
        faint(0.55),
      );
    }
  }
}

function barrel(c: CanvasRenderingContext2D, e: Entity, lw: number, seed: number, scene: Scene): void {
  const r = e.r ?? 0.6;
  const fill = paint(c, e, flashed(e) ?? e.fill, scene.cam.scale);
  draw(c, circlePts(r), seed, fill, lw);
  draw(c, circlePts(r * 0.58), seed + 1, null, lw * 0.7, faint(0.6));
  // A hazard sign on the lid, so it reads as a thing that goes off.
  const s = r * 0.62;
  const tri = [{ x: 0, y: -s }, { x: s * 0.95, y: s * 0.68 }, { x: -s * 0.95, y: s * 0.68 }];
  draw(c, tri, seed + 2, mono ? colours.cream : colours.yellow, lw * 0.8);
  c.fillStyle = colours.ink;
  c.fillRect(-s * 0.09, -s * 0.48, s * 0.18, s * 0.66);
  c.beginPath();
  c.arc(0, s * 0.42, s * 0.1, 0, Math.PI * 2);
  c.fill();
}

function tank(c: CanvasRenderingContext2D, e: Entity, lw: number, hull: number, seed: number, scene: Scene): void {
  const hw = e.hw ?? 1;
  const hh = e.hh ?? 0.7;
  const fill = paint(c, e, flashed(e) ?? e.fill, scene.cam.scale);
  const style = e.style ?? (e.kind === "player" ? "standard" : e.kind === "boss" ? "boss" : "tank");
  const drone = style === "drone";

  if (drone) {
    // A drone: an enemy-red disc with four ink blades and one angry eye.
    // When it commits to a dive it rings red and spins up, which is the
    // moment to shoot it or get out of the way.
    const r = e.r ?? 0.45;
    const dive = e.dive === true;
    if (dive) {
      const pulse = scene.reduced ? 1 : 0.75 + 0.25 * Math.sin(scene.time * 18);
      c.strokeStyle = mono ? colours.ink : colours.enemy;
      c.lineWidth = lw * 1.4;
      c.globalAlpha = pulse;
      c.beginPath();
      c.arc(0, 0, r * 2.35 * pulse, 0, Math.PI * 2);
      c.stroke();
      c.globalAlpha = 1;
    }
    c.save();
    c.rotate(scene.reduced ? 0.4 : scene.time * (dive ? 30 : 14));
    for (let i = 0; i < 4; i++) {
      c.rotate(Math.PI / 2);
      c.fillStyle = colours.ink;
      c.fillRect(0, -0.09, r * 2, 0.18);
      c.fillStyle = mono ? colours.ink : colours.enemy;
      c.fillRect(r * 1.6, -0.11, r * 0.45, 0.22);
    }
    c.restore();
    draw(c, circlePts(r), seed, fill, lw);
    draw(c, circlePts(r * 0.7), seed + 1, null, lw * 0.6, "rgba(25,25,25,0.45)");
    // The eye looks where it is going, with a brow pulled down over it.
    const ex = r * 0.28;
    const er = r * 0.42;
    c.fillStyle = colours.cream;
    c.beginPath();
    c.arc(ex, 0, er, 0, Math.PI * 2);
    c.fill();
    c.strokeStyle = colours.ink;
    c.lineWidth = lw * 0.7;
    c.stroke();
    c.fillStyle = mono ? colours.ink : colours.enemy;
    c.beginPath();
    c.arc(ex + er * 0.3, 0, er * 0.55, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = colours.ink;
    c.beginPath();
    c.arc(ex + er * 0.38, 0, er * 0.25, 0, Math.PI * 2);
    c.fill();
    // The brow: a heavy V over the front of the eye, cutting into it.
    c.lineWidth = lw * 1.3;
    c.beginPath();
    c.moveTo(ex - er * 0.5, -er * 1.05);
    c.lineTo(ex + er * 0.75, -er * 0.15);
    c.lineTo(ex + er * 0.75, er * 0.15);
    c.lineTo(ex - er * 0.5, er * 1.05);
    c.stroke();
    return;
  }

  // Tracks first, sitting proud of the hull on both sides. Bulwark and the
  // gunner run wide ones; the scout and hunter narrow.
  const trackH = style === "bulwark" || style === "gunner" || style === "boss" ? 0.44 : style === "scout" || style === "hunter" ? 0.26 : 0.34;
  draw(c, rectPts(-hw * 0.98, -hh - trackH * 0.42, hw * 1.96, trackH, 0.1), seed + 1, colours.ink, 0);
  draw(c, rectPts(-hw * 0.98, hh - trackH * 0.58, hw * 1.96, trackH, 0.1), seed + 2, colours.ink, 0);
  const roll = e.roll ?? 0;
  c.strokeStyle = "rgba(255,255,240,0.45)";
  c.lineWidth = lw * 0.5;
  c.beginPath();
  for (let i = 0; i < 6; i++) {
    const x = -hw * 0.9 + (((i * 0.33 + roll) % 2) * hw * 0.95);
    c.moveTo(x, -hh - trackH * 0.36);
    c.lineTo(x, -hh + trackH * 0.46);
    c.moveTo(x, hh - trackH * 0.52);
    c.lineTo(x, hh + trackH * 0.3);
  }
  c.stroke();

  // The hull. Each tank has its own outline.
  const hullPts = hullShape(style, hw, hh);
  draw(c, hullPts, seed, fill, lw);

  // Plates and rivets on the heavy ones, an antenna on the light ones.
  if (style === "bulwark" || style === "gunner") {
    c.fillStyle = "rgba(25,25,25,0.45)";
    for (let i = -2; i <= 2; i++) {
      for (const sy of [-1, 1]) {
        c.beginPath();
        c.arc(i * hw * 0.38, sy * hh * 0.72, 0.06, 0, Math.PI * 2);
        c.fill();
      }
    }
    line(c, seed + 40, { x: -hw * 0.2, y: -hh * 0.85 }, { x: -hw * 0.2, y: hh * 0.85 }, lw * 0.5, faint(0.4));
  }
  if (style === "scout" || style === "hunter") {
    line(c, seed + 41, { x: -hw * 0.7, y: -hh * 0.5 }, { x: -hw * 1.35, y: -hh * 1.1 }, lw * 0.5, colours.ink);
  }
  if (style === "skipper") {
    // A fin at the back, for the look of a thing that skips.
    draw(c, [{ x: -hw, y: -hh * 0.3 }, { x: -hw * 1.3, y: -hh * 0.6 }, { x: -hw * 1.3, y: hh * 0.6 }, { x: -hw, y: hh * 0.3 }], seed + 42, fill, lw * 0.8);
  }

  // A stripe down the hull, so a spin is legible at a glance.
  if (!mono && style !== "wraith") {
    c.fillStyle = "rgba(25,25,25,0.14)";
    c.fillRect(-hw * 0.5, -hh * 0.35, hw * 1.2, hh * 0.7);
  }

  if (e.paint?.decal && e.paint.decal !== "none") decal(c, e.paint.decal, hw, hh, seed);

  // An arrowhead on the nose, so the front reads even with the lamps
  // against a wall. Yours is yellow and bold; theirs a fainter ink one.
  nose(c, e.kind === "player", style, hw, hh, lw, seed);

  lights(c, e, hw, hh, lw);

  // The turret turns in world space, so undo the hull's rotation.
  c.save();
  c.rotate((e.turret ?? 0) - hull);
  const kick = (e.recoil ?? 0) * 0.5;
  const barrelLen = (style === "boss" ? 2.1 : style === "wraith" || style === "skipper" ? 1.9 : style === "bulwark" || style === "gunner" ? 1.25 : style === "scout" || style === "hunter" ? 1.2 : 1.5) * (hw / 1.1);
  const barrelW = style === "bulwark" || style === "gunner" ? 0.46 : style === "wraith" || style === "skipper" ? 0.22 : 0.34;
  draw(c, rectPts(-0.18 - kick, -barrelW / 2, barrelLen, barrelW, 0.08), seed + 3, colours.ink, 0);
  if (style === "bulwark" || style === "gunner") {
    // A muzzle brake: two ticks near the end.
    c.fillStyle = colours.ink;
    c.fillRect(-0.18 - kick + barrelLen - 0.34, -barrelW * 0.85, 0.12, barrelW * 1.7);
    c.fillRect(-0.18 - kick + barrelLen - 0.14, -barrelW * 0.85, 0.12, barrelW * 1.7);
  }
  if (style === "boss") {
    c.save();
    c.rotate(0.42);
    draw(c, rectPts(-0.18 - kick, -0.14, barrelLen * 0.8, 0.28, 0.07), seed + 4, colours.ink, 0);
    c.restore();
    c.save();
    c.rotate(-0.42);
    draw(c, rectPts(-0.18 - kick, -0.14, barrelLen * 0.8, 0.28, 0.07), seed + 5, colours.ink, 0);
    c.restore();
  }
  const capR = hh * (style === "wraith" ? 0.55 : style === "bulwark" || style === "gunner" ? 0.8 : 0.72);
  const cap = e.kind === "player" ? colours.yellow : colours.cream;
  if (style === "wraith") {
    draw(c, [{ x: capR, y: 0 }, { x: -capR * 0.6, y: -capR * 0.9 }, { x: -capR * 0.9, y: 0 }, { x: -capR * 0.6, y: capR * 0.9 }], seed + 6, mono ? colours.cream : cap, lw);
  } else {
    draw(c, circlePts(capR), seed + 6, mono ? colours.cream : cap, lw);
  }
  // A sight line, so you can read where the gun points without the barrel.
  line(c, seed + 7, { x: 0, y: 0 }, { x: capR, y: 0 }, lw * 0.8, colours.ink);
  c.restore();

  wear(c, e, hw, hh, lw, seed);
  // The player reads its own tank rather than the corners of the screen.
  if (e.gun) readout(c, e, hh + trackH * 0.4, lw, hull, scene);
}

/**
 * A chevron band across the nose, pointing the way the tank drives. It sits
 * forward of the turret cap and its arms reach out to the tracks, so the gun
 * can point anywhere and the front still shows.
 */
function nose(c: CanvasRenderingContext2D, player: boolean, style: string, hw: number, hh: number, lw: number, seed: number): void {
  const x1 = hw * 0.98;
  const x0 = hw * 0.6;
  const t = hw * (player ? 0.24 : 0.16);
  const h = hh * (style === "wraith" ? 0.42 : player ? 0.86 : 0.62);
  const pts = [
    { x: x1, y: 0 }, { x: x0, y: -h }, { x: x0 - t, y: -h },
    { x: x1 - t, y: 0 }, { x: x0 - t, y: h }, { x: x0, y: h },
  ];
  if (player) draw(c, pts, seed + 30, mono ? colours.cream : colours.yellow, lw * 0.9);
  else draw(c, pts, seed + 30, "rgba(25,25,25,0.4)", 0);
}

/** The outline of each hull, nose to +x. */
function hullShape(style: string, hw: number, hh: number): Pt[] {
  switch (style) {
    case "scout":
    case "hunter":
      return [
        { x: -hw, y: -hh * 0.85 }, { x: hw * 0.55, y: -hh }, { x: hw * 1.05, y: -hh * 0.35 },
        { x: hw * 1.05, y: hh * 0.35 }, { x: hw * 0.55, y: hh }, { x: -hw, y: hh * 0.85 },
      ];
    case "bulwark":
    case "gunner":
      // Square at the back, a short bevel on the nose.
      return [
        { x: -hw, y: -hh }, { x: hw * 0.8, y: -hh }, { x: hw, y: -hh * 0.62 },
        { x: hw, y: hh * 0.62 }, { x: hw * 0.8, y: hh }, { x: -hw, y: hh },
      ];
    case "skipper":
      return [
        { x: -hw * 0.75, y: -hh }, { x: hw * 0.75, y: -hh }, { x: hw, y: -hh * 0.5 }, { x: hw, y: hh * 0.5 },
        { x: hw * 0.75, y: hh }, { x: -hw * 0.75, y: hh }, { x: -hw, y: hh * 0.5 }, { x: -hw, y: -hh * 0.5 },
      ];
    case "wraith":
      return [
        { x: -hw * 0.9, y: -hh }, { x: hw * 0.2, y: -hh * 0.9 }, { x: hw * 1.1, y: 0 },
        { x: hw * 0.2, y: hh * 0.9 }, { x: -hw * 0.9, y: hh }, { x: -hw * 0.6, y: 0 },
      ];
    default:
      return [
        { x: -hw, y: -hh }, { x: hw * 0.68, y: -hh }, { x: hw, y: -hh * 0.5 },
        { x: hw, y: hh * 0.5 }, { x: hw * 0.68, y: hh }, { x: -hw, y: hh },
      ];
  }
}

/**
 * Headlights on the nose, tail lights on the back, lit like a real
 * vehicle's: lamps and a cone ahead when driving forward, red behind when
 * reversing or standing on the brakes.
 */
function lights(c: CanvasRenderingContext2D, e: Entity, hw: number, hh: number, lw: number): void {
  const throttle = e.throttle ?? 0;
  const speed = e.speed ?? 0;
  const ahead = throttle > 0.1;
  const braking = throttle < -0.1 || (throttle <= 0.05 && speed > 1.5);
  const lampW = 0.16;
  const lampH = 0.22;
  for (const sy of [-1, 1]) {
    const y = sy * hh * 0.58;
    if (ahead && !mono) {
      // A beam that thins out to nothing, the way light does on paper.
      const reach = 2.6;
      const beam = c.createLinearGradient(hw, y, hw + reach, y);
      beam.addColorStop(0, "rgba(255,237,0,0.32)");
      beam.addColorStop(0.45, "rgba(255,237,0,0.12)");
      beam.addColorStop(1, "rgba(255,237,0,0)");
      c.fillStyle = beam;
      c.beginPath();
      c.moveTo(hw, y - lampH / 2);
      c.quadraticCurveTo(hw + reach * 0.6, y - 0.95, hw + reach, y - 1.05);
      c.lineTo(hw + reach, y + 1.05);
      c.quadraticCurveTo(hw + reach * 0.6, y + 0.95, hw, y + lampH / 2);
      c.closePath();
      c.fill();
    }
    c.fillStyle = ahead && !mono ? colours.acid : colours.cream;
    c.fillRect(hw - lampW * 0.6, y - lampH / 2, lampW, lampH);
    c.strokeStyle = colours.ink;
    c.lineWidth = lw * 0.5;
    c.strokeRect(hw - lampW * 0.6, y - lampH / 2, lampW, lampH);

    const tail = mono ? (braking ? colours.ink : colours.cream) : braking ? "#ff3b2a" : "#7a2a22";
    c.fillStyle = tail;
    c.fillRect(-hw - lampW * 0.4, y - lampH / 2, lampW, lampH);
    c.strokeRect(-hw - lampW * 0.4, y - lampH / 2, lampW, lampH);
    if (braking && !mono) {
      c.fillStyle = "rgba(255,59,42,0.25)";
      c.beginPath();
      c.arc(-hw - lampW * 0.4, y, 0.42, 0, Math.PI * 2);
      c.fill();
    }
  }
}

/** Graffiti on the hull, behind the turret so it reads. */
function decal(c: CanvasRenderingContext2D, kind: string, hw: number, hh: number, seed: number): void {
  c.save();
  c.beginPath();
  c.rect(-hw, -hh, hw * 2, hh * 2);
  c.clip();
  const x = -hw * 0.5;
  const size = Math.min(hh * 0.75, hw * 0.45);
  const cream = mono ? colours.cream : colours.cream;
  const yellow = mono ? colours.cream : colours.yellow;
  switch (kind) {
    case "stripe":
      c.fillStyle = cream;
      c.beginPath();
      c.moveTo(-hw * 0.2, -hh);
      c.lineTo(hw * 0.15, -hh);
      c.lineTo(-hw * 0.45, hh);
      c.lineTo(-hw * 0.8, hh);
      c.closePath();
      c.fill();
      break;
    case "star": {
      const pts: Pt[] = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 ? size * 0.42 : size;
        const a = -Math.PI / 2 + (i / 10) * Math.PI * 2;
        pts.push({ x: x + Math.cos(a) * r, y: Math.sin(a) * r });
      }
      draw(c, pts, seed + 50, yellow, 0.05);
      break;
    }
    case "skull":
      draw(c, circlePts(size * 0.75, x, -size * 0.1), seed + 51, cream, 0.05);
      c.fillStyle = colours.ink;
      c.beginPath();
      c.arc(x - size * 0.28, -size * 0.2, size * 0.18, 0, Math.PI * 2);
      c.arc(x + size * 0.28, -size * 0.2, size * 0.18, 0, Math.PI * 2);
      c.fill();
      c.fillRect(x - size * 0.3, size * 0.45, size * 0.6, size * 0.22);
      break;
    case "bolt":
      draw(c, [
        { x: x + size * 0.2, y: -size }, { x: x - size * 0.45, y: size * 0.1 }, { x: x, y: size * 0.05 },
        { x: x - size * 0.25, y: size }, { x: x + size * 0.5, y: -size * 0.15 }, { x: x + size * 0.05, y: -size * 0.1 },
      ], seed + 52, yellow, 0.05);
      break;
    case "seven":
      c.fillStyle = cream;
      c.font = `800 ${size * 2}px Nunito, ui-sans-serif, sans-serif`;
      c.textAlign = "center";
      c.textBaseline = "middle";
      c.fillText("7", x, 0.04);
      break;
    case "flames":
      for (const sy of [-1, 1]) {
        const pts: Pt[] = [{ x: -hw, y: sy * hh * 0.95 }];
        for (let i = 0; i < 5; i++) {
          pts.push({ x: -hw + i * hw * 0.32 + hw * 0.16, y: sy * hh * (i % 2 ? 0.9 : 0.35) });
        }
        pts.push({ x: -hw + hw * 1.7, y: sy * hh * 0.95 });
        draw(c, pts, seed + 53 + (sy + 1), mono ? cream : "#d6362b", 0.05);
        pts.forEach((pt) => (pt.y = sy * (Math.abs(pt.y) * 0.75 + hh * 0.2)));
        draw(c, pts, seed + 55 + (sy + 1), yellow, 0);
      }
      break;
    case "flower":
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        draw(c, circlePts(size * 0.38, x + Math.cos(a) * size * 0.5, Math.sin(a) * size * 0.5), seed + 60 + i, cream, 0.05);
      }
      draw(c, circlePts(size * 0.3, x, 0), seed + 66, yellow, 0.05);
      break;
    case "camo": {
      // Blotches all over, the two other tones of the paper.
      const blobs = [[-0.7, -0.5, 0.42], [0.1, -0.6, 0.3], [-0.2, 0.4, 0.36], [0.7, 0.3, 0.44], [-0.8, 0.5, 0.26], [0.5, -0.2, 0.22]];
      blobs.forEach(([bx, by, br], i) => {
        const pts = circlePts(size * (br as number) * 1.3, (bx as number) * hw, (by as number) * hh);
        pts.forEach((pt, k) => {
          pt.x += Math.sin(k * 1.7 + i) * size * 0.08;
          pt.y += Math.cos(k * 1.3 + i) * size * 0.08;
        });
        draw(c, pts, seed + 70 + i, i % 2 ? cream : "rgba(25,25,25,0.35)", 0);
      });
      break;
    }
    case "roundel":
      draw(c, circlePts(size * 0.95, x, 0), seed + 80, cream, 0.05);
      draw(c, circlePts(size * 0.62, x, 0), seed + 81, mono ? colours.ink : "#d6362b", 0);
      draw(c, circlePts(size * 0.3, x, 0), seed + 82, cream, 0);
      break;
    case "chevrons":
      c.fillStyle = cream;
      for (let i = 0; i < 3; i++) {
        const cx = -hw * 0.85 + i * hw * 0.42;
        c.beginPath();
        c.moveTo(cx, -hh);
        c.lineTo(cx + hw * 0.22, -hh);
        c.lineTo(cx + hw * 0.5, 0);
        c.lineTo(cx + hw * 0.22, hh);
        c.lineTo(cx, hh);
        c.lineTo(cx + hw * 0.28, 0);
        c.closePath();
        c.fill();
      }
      break;
    case "checks":
      c.fillStyle = cream;
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 3; j++) {
          if ((i + j) % 2) continue;
          c.fillRect(-hw + i * hw * 0.5, -hh + j * hh * 0.67, hw * 0.5, hh * 0.67);
        }
      }
      break;
    case "heart": {
      const pts: Pt[] = [];
      for (let i = 0; i < 24; i++) {
        const t = (i / 24) * Math.PI * 2;
        const hx = 16 * Math.pow(Math.sin(t), 3);
        const hy = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
        pts.push({ x: x + (hx / 17) * size * 0.9, y: (hy / 17) * size * 0.9 });
      }
      draw(c, pts, seed + 90, mono ? cream : "#d6362b", 0.05);
      break;
    }
    case "wave":
      c.strokeStyle = cream;
      c.lineWidth = size * 0.28;
      c.lineCap = "round";
      for (const dy of [-hh * 0.45, hh * 0.45]) {
        c.beginPath();
        for (let i = 0; i <= 12; i++) {
          const px = -hw + (i / 12) * hw * 2;
          const py = dy + Math.sin(i * 0.9) * hh * 0.22;
          if (i === 0) c.moveTo(px, py);
          else c.lineTo(px, py);
        }
        c.stroke();
      }
      break;
  }
  c.restore();
}

function turretPost(c: CanvasRenderingContext2D, e: Entity, lw: number, seed: number, scene: Scene): void {
  const r = e.r ?? 0.8;
  const fill = paint(c, e, flashed(e) ?? e.fill, scene.cam.scale);
  draw(c, circlePts(r), seed, fill, lw);

  if (e.style === "mortar") {
    // A mortar pit: sandbags round a fat tube pointing straight up.
    draw(c, circlePts(r * 0.62), seed + 1, colours.ink, 0);
    draw(c, circlePts(r * 0.38), seed + 2, mono ? colours.cream : "#5a1410", lw * 0.6);
    c.fillStyle = faint(0.35);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + (e.recoil ?? 0);
      c.beginPath();
      c.arc(Math.cos(a) * r * 0.86, Math.sin(a) * r * 0.86, 0.11, 0, Math.PI * 2);
      c.fill();
    }
    wear(c, e, r * 0.8, r * 0.8, lw, seed);
    return;
  }

  c.save();
  c.rotate(e.turret ?? 0);
  const kick = (e.recoil ?? 0) * 0.4;
  draw(c, rectPts(-0.14 - kick, -0.15, r * 2.1, 0.3, 0.07), seed + 1, colours.ink, 0);
  draw(c, circlePts(r * 0.52), seed + 2, colours.cream, lw);
  c.restore();

  wear(c, e, r * 0.8, r * 0.8, lw, seed);
}

/**
 * Damage, drawn on the thing: dents and cracks appear as the hits land, and
 * by the last one it is more scar than paint. No bars over anyone's head.
 */
function wear(c: CanvasRenderingContext2D, e: Entity, hw: number, hh: number, lw: number, seed: number): void {
  if (e.maxHp <= 1 || e.hp >= e.maxHp) return;
  const lost = Math.min(1, Math.max(0, (e.maxHp - e.hp) / e.maxHp));
  const marks = Math.min(7, Math.ceil(lost * 7));
  c.save();
  const jitterAt = (i: number, k: number) => jitter(seed + 100, i * 3 + k);
  for (let i = 0; i < marks; i++) {
    const x = jitterAt(i, 0) * hw * 0.8;
    const y = jitterAt(i, 1) * hh * 0.8;
    const a = jitterAt(i, 2) * Math.PI;
    // A dent: a dark blot with a bright edge on one side.
    c.fillStyle = mono ? "rgba(25,25,25,0.5)" : "rgba(25,25,25,0.42)";
    c.beginPath();
    c.ellipse(x, y, 0.16 + hw * 0.06, 0.1 + hh * 0.05, a, 0, Math.PI * 2);
    c.fill();
    // And a crack out of it.
    const len = 0.25 + lost * hw * 0.6;
    line(c, seed + 110 + i, { x, y }, { x: x + Math.cos(a) * len, y: y + Math.sin(a) * len }, lw * 0.55, "rgba(25,25,25,0.7)");
    if (i % 2 === 0) {
      line(c, seed + 120 + i, { x: x + Math.cos(a) * len * 0.5, y: y + Math.sin(a) * len * 0.5 }, { x: x + Math.cos(a + 1.2) * len * 0.5, y: y + Math.sin(a + 1.2) * len * 0.5 }, lw * 0.45, "rgba(25,25,25,0.6)");
    }
  }
  // Scorch, once it is mostly gone.
  if (lost >= 0.66 && !mono) {
    c.fillStyle = `rgba(25,25,25,${(lost - 0.6) * 0.5})`;
    c.beginPath();
    c.ellipse(-hw * 0.3, 0, hw * 0.6, hh * 0.7, 0, 0, Math.PI * 2);
    c.fill();
  }
  c.restore();
}

/**
 * The works: a shed with a saw-tooth roof and a chimney, and a door that
 * glows when it is about to put another one out. It wears its damage like
 * everything else, and smokes harder the closer it is to coming down.
 */
function factory(c: CanvasRenderingContext2D, e: Entity, lw: number, seed: number, scene: Scene): void {
  const hw = e.hw ?? 1.6;
  const hh = e.hh ?? 1.6;
  const fill = paint(c, e, flashed(e) ?? e.fill, scene.cam.scale);
  draw(c, rectPts(-hw, -hh, hw * 2, hh * 2, 0.14), seed, fill, lw);

  // Saw-tooth roof, the way a works is drawn from above.
  const ink = faint(0.4);
  for (let i = -2; i <= 2; i++) {
    const x = (i * hw) / 2.6;
    line(c, seed + 10 + i, { x, y: -hh * 0.85 }, { x: x + hw * 0.22, y: hh * 0.85 }, lw * 0.5, ink);
  }

  // The chimney, upwind corner, always smoking a little.
  const cx = -hw * 0.62;
  const cy = -hh * 0.62;
  draw(c, circlePts(hh * 0.24, cx, cy), seed + 20, mono ? colours.cream : "#6d6a60", lw * 0.7);
  c.fillStyle = colours.ink;
  c.beginPath();
  c.arc(cx, cy, hh * 0.1, 0, Math.PI * 2);
  c.fill();

  // The door it sends them out of, lit when one is on the way.
  const ready = (e.ttl ?? 0) < 1.2;
  const dw = hw * 0.5;
  draw(c, rectPts(-dw, hh * 0.45, dw * 2, hh * 0.5, 0.06), seed + 30, ready && !mono ? colours.acid : colours.ink, lw * 0.6);

  wear(c, e, hw, hh, lw, seed);
}

/**
 * The player's gauges, over the tank instead of in a corner: armour, boost,
 * and what is in the breech. They stay upright however the hull and the
 * camera are turned, because a gauge you have to read sideways is not a
 * gauge, and they are drawn crisp rather than sketched; everything else on
 * the paper is hand-drawn, but an instrument reads as printed on the plate.
 */
function readout(
  c: CanvasRenderingContext2D,
  e: Entity,
  above: number,
  lw: number,
  hull: number,
  scene: Scene,
): void {
  const maxHp = Math.max(1, e.maxHp);
  const hp = Math.max(0, Math.min(maxHp, e.hp));
  const iconW = 0.6;
  const barW = 1.74;
  const gap = 0.15;
  const armH = 0.27;
  const boostH = 0.16;
  const split = 0.075;
  const x0 = -(iconW + gap + barW) / 2;
  const barX = x0 + iconW + gap;
  const top = -above - 1.02;

  const wasBucket = bucket;
  bucket = 0;
  c.save();
  c.rotate(-hull - scene.cam.rot);

  gauge(c, barX, top, barW, armH, hp / maxHp, mono ? colours.cream : colours.acid, lw);
  // A tick for every hit it can take, while they are still far enough apart
  // to tell one from another. Cut in cream so they show on the bright part
  // of the bar and on the dark part alike.
  if (maxHp <= 12) {
    c.strokeStyle = "rgba(255,255,240,0.7)";
    c.lineWidth = lw * 0.5;
    c.beginPath();
    for (let i = 1; i < maxHp; i++) {
      const x = barX + (barW * i) / maxHp;
      c.moveTo(x, top + 0.045);
      c.lineTo(x, top + armH - 0.045);
    }
    c.stroke();
  }

  gauge(c, barX, top + armH + split, barW, boostH, e.boost ?? 0, colours.cream, lw);

  // The gun: a token with the rounds it fires drawn on it, so a glance says
  // three light ones, or one heavy, or one that will come back.
  const gun = e.gun;
  const cx = x0 + iconW / 2;
  const cy = top + (armH + split + boostH) / 2;
  const half = iconW / 2;
  c.beginPath();
  plainRect(c, cx - half, cy - half, iconW, iconW, 0.08);
  c.fillStyle = colours.cream;
  c.fill();
  c.strokeStyle = colours.ink;
  c.lineWidth = lw * 0.65;
  c.stroke();

  if (gun) {
    const shots = Math.max(1, Math.min(3, gun.shots));
    const wide = shots > 1;
    const sw = (gun.heavy ? 0.095 : 0.062) * (wide ? 0.85 : 1.4);
    const sh = gun.heavy ? 0.38 : 0.31;
    for (let i = 0; i < shots; i++) {
      const sx = cx + (wide ? (i - (shots - 1) / 2) * 0.145 : 0);
      shellMark(c, sx, cy + 0.03, sw, sh, colours.ink, 900 + i);
    }
    if (gun.bouncy) {
      c.strokeStyle = colours.ink;
      c.lineWidth = lw * 0.55;
      c.beginPath();
      c.arc(cx, cy - 0.02, half * 0.8, Math.PI * 1.12, Math.PI * 1.88);
      c.stroke();
    }
    // How long the gun stays this way. Nothing there means it always will.
    if (gun.left > 0) {
      const y = cy + half + 0.06;
      c.fillStyle = mono ? colours.ink : colours.acid;
      c.fillRect(cx - half, y, iconW * gun.left, 0.07);
      c.strokeStyle = colours.ink;
      c.lineWidth = lw * 0.35;
      c.strokeRect(cx - half, y, iconW, 0.07);
    }
  }

  c.restore();
  bucket = wasBucket;
}

/** A frame with a fill in it. */
function gauge(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  fill: number,
  colour: string,
  lw: number,
): void {
  // A dark track with a bright bar in it: at six pixels tall, filled-versus-
  // empty reads at a glance where two colours on cream do not.
  c.beginPath();
  plainRect(c, x, y, w, h, h / 2);
  c.fillStyle = "rgba(25,25,25,0.85)";
  c.fill();
  const amount = Math.max(0, Math.min(1, fill));
  if (amount > 0) {
    c.save();
    c.clip();
    c.fillStyle = colour;
    c.fillRect(x, y, w * amount, h);
    c.restore();
  }
  c.beginPath();
  plainRect(c, x, y, w, h, h / 2);
  c.strokeStyle = colours.ink;
  c.lineWidth = lw * 0.5;
  c.stroke();
}

/** A rounded rectangle with none of the wobble. Adds to the open path. */
function plainRect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  c.moveTo(x + rr, y);
  c.arcTo(x + w, y, x + w, y + h, rr);
  c.arcTo(x + w, y + h, x, y + h, rr);
  c.arcTo(x, y + h, x, y, rr);
  c.arcTo(x, y, x + w, y, rr);
  c.closePath();
}

/** A car that has been driven over: flat, wider, and done. */
function wreck(c: CanvasRenderingContext2D, e: Entity, lw: number, seed: number): void {
  const hw = (e.hw ?? 0.95) * 1.15;
  const hh = (e.hh ?? 0.5) * 1.25;
  c.globalAlpha *= 0.85;
  draw(c, rectPts(-hw, -hh, hw * 2, hh * 2, 0.16), seed, mono ? colours.cream : "#b7433a", lw * 0.7);
  // Crumple lines across it.
  for (let i = -2; i <= 2; i++) {
    line(c, seed + 130 + i, { x: i * hw * 0.35, y: -hh }, { x: i * hw * 0.35 + jitter(seed, i) * 0.3, y: hh }, lw * 0.45, "rgba(25,25,25,0.55)");
  }
  c.fillStyle = "rgba(25,25,25,0.35)";
  c.fillRect(-hw * 0.5, -hh * 0.3, hw, hh * 0.6);
  c.globalAlpha = 1;
}

function shell(c: CanvasRenderingContext2D, e: Entity, lw: number, seed: number, scene: Scene): void {
  const r = e.r ?? 0.18;
  // A shell of yours that has bounced is drawn as a threat, because it is.
  const mine = e.team === "player" && !e.stray;
  if (!mono) {
    // A glow behind, and a short trail so a fast shell reads as a line.
    c.fillStyle = mine ? "rgba(255,237,0,0.2)" : "rgba(214,54,43,0.18)";
    c.beginPath();
    c.ellipse(-r * 1.6, 0, r * 3.2, r * 1.5, 0, 0, Math.PI * 2);
    c.fill();
  }
  if (e.mortar) {
    draw(c, circlePts(r), seed, paint(c, e, e.fill, scene.cam.scale), lw * 0.7);
    c.strokeStyle = colours.cream;
    c.lineWidth = lw * 0.5;
    c.beginPath();
    c.arc(0, 0, r * 0.5, 0, Math.PI * 2);
    c.stroke();
    return;
  }
  // The same capsule as on the pickup token: a brass body and a dark tip,
  // pointing the way it is going.
  c.save();
  c.rotate(Math.PI / 2);
  const hw = r * 0.85;
  const hh = r * 2.2;
  shellMark(c, 0, 0, hw, hh, mono ? colours.ink : mine ? colours.ink : "#5a1410", seed);
  c.restore();
  if ((e.bounces ?? 0) > 0 || e.stray) {
    // A bouncer wears a ring, so you know it is coming back, and once it
    // has, the ring goes red: that one is yours and it will still bite.
    c.strokeStyle = e.stray && !mono ? "#d6362b" : colours.ink;
    c.lineWidth = lw * (e.stray ? 0.8 : 0.5);
    c.beginPath();
    c.arc(0, 0, r * 1.6, 0, Math.PI * 2);
    c.stroke();
  }
}

function pickup(c: CanvasRenderingContext2D, e: Entity, lw: number, seed: number, scene: Scene): void {
  const bob = scene.reduced ? 0 : Math.sin(scene.time * 2.6) * 0.08;
  const pulse = scene.reduced ? 0.5 : (Math.sin(scene.time * 4) + 1) / 2;
  c.translate(0, bob);
  const r = (e.r ?? 0.55) * 1.45;
  const perk = e.perk;
  const halo = perk === "repair" ? "214,54,43" : perk === "shield" ? "2,96,42" : "255,210,18";
  if (!mono) {
    c.beginPath();
    c.arc(0, 0, r * (1.15 + pulse * 0.25), 0, Math.PI * 2);
    c.fillStyle = `rgba(${halo},${0.16 + pulse * 0.12})`;
    c.fill();
    c.beginPath();
    c.arc(0, 0, r, 0, Math.PI * 2);
    c.fillStyle = `rgba(${halo},0.3)`;
    c.fill();
  }
  // A dropped pickup is on a fuse: the ring closes as the time runs out.
  if (e.fuse && e.ttl !== undefined) {
    const left = Math.max(0, Math.min(1, e.ttl / e.fuse));
    c.beginPath();
    c.arc(0, 0, r * 1.3, -Math.PI / 2, -Math.PI / 2 + left * Math.PI * 2);
    c.strokeStyle = left < 0.25 && !scene.reduced && Math.floor(scene.time * 6) % 2 === 0 ? colours.acid : colours.ink;
    c.lineWidth = lw * 1.2;
    c.stroke();
  }
  c.setLineDash([0.2, 0.16]);
  sketch(c, circlePts(r), seed + 9);
  c.strokeStyle = colours.ink;
  c.lineWidth = lw * 0.7;
  c.stroke();
  c.setLineDash([]);

  // The token: a cream tile, and on it the thing itself: the shells you
  // will fire, or a cross, or a shield, so you can read it from across
  // the yard. It stays upright whichever way the camera is turned.
  const half = r * 0.62;
  c.save();
  c.rotate(-scene.cam.rot);
  draw(c, rectPts(-half, -half, half * 2, half * 2, half * 0.3), seed, flashed(e) ?? colours.cream, lw);
  const ink = colours.ink;
  const what = perk ?? e.power ?? "cannon";
  if (what === "triple") {
    for (let i = -1; i <= 1; i++) shellMark(c, i * half * 0.55, 0, half * 0.24, half * 0.62, ink, seed + i);
  } else if (what === "heavy") {
    shellMark(c, 0, 0, half * 0.5, half * 1.2, ink, seed);
  } else if (what === "bouncer") {
    shellMark(c, -half * 0.25, half * 0.2, half * 0.24, half * 0.6, ink, seed);
    c.strokeStyle = ink;
    c.lineWidth = lw * 1.1;
    c.beginPath();
    c.arc(half * 0.1, -half * 0.1, half * 0.62, Math.PI * 1.05, Math.PI * 1.95);
    c.stroke();
    c.beginPath();
    c.moveTo(half * 0.62, -half * 0.42);
    c.lineTo(half * 0.72, -half * 0.05);
    c.lineTo(half * 0.36, -half * 0.12);
    c.closePath();
    c.fillStyle = ink;
    c.fill();
  } else if (what === "repair") {
    c.fillStyle = mono ? ink : "#d6362b";
    c.fillRect(-half * 0.2, -half * 0.68, half * 0.4, half * 1.36);
    c.fillRect(-half * 0.68, -half * 0.2, half * 1.36, half * 0.4);
  } else if (what === "shield") {
    draw(c, [
      { x: -half * 0.6, y: -half * 0.6 }, { x: half * 0.6, y: -half * 0.6 }, { x: half * 0.6, y: half * 0.1 },
      { x: 0, y: half * 0.7 }, { x: -half * 0.6, y: half * 0.1 },
    ], seed + 8, mono ? colours.cream : colours.green, lw);
    c.fillStyle = colours.cream;
    c.fillRect(-half * 0.08, -half * 0.4, half * 0.16, half * 0.7);
    c.fillRect(-half * 0.36, -half * 0.18, half * 0.72, half * 0.16);
  }
  // Its name under the tile, small, so nobody has to guess.
  c.fillStyle = ink;
  c.font = `800 ${half * 0.5}px Nunito, ui-sans-serif, sans-serif`;
  c.textAlign = "center";
  c.textBaseline = "top";
  c.fillText(LABEL[what] ?? what.toUpperCase(), 0, half + lw * 3);
  c.restore();
}

/** One shell, nose up: a capsule with a dark tip. */
function shellMark(c: CanvasRenderingContext2D, x: number, y: number, hw: number, hh: number, ink: string, seed: number): void {
  const pts: Pt[] = [];
  const n = 8;
  for (let i = 0; i <= n; i++) {
    const a = Math.PI + (i / n) * Math.PI;
    pts.push({ x: x + Math.cos(a) * hw, y: y - hh * 0.35 + Math.sin(a) * hw * 1.4 });
  }
  pts.push({ x: x + hw, y: y + hh * 0.55 }, { x: x - hw, y: y + hh * 0.55 });
  draw(c, pts, seed + 70, mono ? colours.cream : ink === "#5a1410" ? "#d6362b" : colours.yellow, 0.035);
  c.fillStyle = ink;
  c.fillRect(x - hw, y + hh * 0.3, hw * 2, hh * 0.25);
}

const LABEL: Record<string, string> = {
  triple: "TRIPLE",
  bouncer: "BOUNCER",
  heavy: "HEAVY",
  repair: "REPAIR",
  shield: "SHIELD",
};

function exitPad(c: CanvasRenderingContext2D, e: Entity, lw: number, seed: number, scene: Scene): void {
  const r = e.r ?? 1;
  const pulse = scene.reduced ? 0.5 : (Math.sin(scene.time * 3.4) + 1) / 2;
  const fill = e.open
    ? mono
      ? hatch(c, scene.cam.scale)
      : `rgba(2,96,42,${0.35 + pulse * 0.35})`
    : "rgba(25,25,25,0.12)";
  c.setLineDash(e.open ? [] : [0.24, 0.2]);
  draw(c, circlePts(r), seed, fill, lw, e.open ? (mono ? colours.ink : colours.green) : "rgba(25,25,25,0.45)");
  c.setLineDash([]);

  const ink = e.open ? colours.ink : "rgba(25,25,25,0.4)";
  for (let i = 0; i < 2; i++) {
    const y = -0.3 + i * 0.42 - (e.open ? pulse * 0.12 : 0);
    line(c, seed + 10 + i, { x: -0.34, y: y + 0.24 }, { x: 0, y: y - 0.06 }, lw * 1.1, ink);
    line(c, seed + 12 + i, { x: 0, y: y - 0.06 }, { x: 0.34, y: y + 0.24 }, lw * 1.1, ink);
  }
}

// ---- particles -------------------------------------------------------------

function worldParticles(c: CanvasRenderingContext2D, scene: Scene): void {
  const lw = 1.6 / scene.cam.scale;
  for (const p of scene.particles.all) {
    if (!p.live || p.kind === "tread" || p.kind === "score") continue;
    const t = p.age / p.life;
    const fade = 1 - t;
    const colour = mono ? colours.ink : p.colour;

    if (p.kind === "ring") {
      c.globalAlpha = fade;
      c.strokeStyle = colour;
      c.lineWidth = lw * 2 * fade;
      c.beginPath();
      c.arc(p.x, p.y, p.size * (0.35 + t * 1.1), 0, Math.PI * 2);
      c.stroke();
      continue;
    }
    if (p.kind === "smoke") {
      c.globalAlpha = fade * 0.4;
      c.fillStyle = colour;
      c.beginPath();
      c.arc(p.x, p.y, p.size * (0.6 + t * 1.5), 0, Math.PI * 2);
      c.fill();
      continue;
    }
    if (p.kind === "spark") {
      c.globalAlpha = fade;
      c.strokeStyle = colour;
      c.lineWidth = lw;
      c.beginPath();
      c.moveTo(p.x, p.y);
      c.lineTo(p.x - p.vx * 0.03, p.y - p.vy * 0.03);
      c.stroke();
      continue;
    }
    // chip: a scrap of paper, so it gets an edge.
    c.globalAlpha = Math.min(1, fade * 2);
    c.save();
    c.translate(p.x, p.y);
    c.rotate(p.angle);
    c.fillStyle = mono ? colours.cream : p.colour;
    c.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
    c.strokeStyle = colours.ink;
    c.lineWidth = lw * 0.6;
    c.strokeRect(-p.size / 2, -p.size / 2, p.size, p.size);
    c.restore();
  }
  c.globalAlpha = 1;
}

function screenParticles(c: CanvasRenderingContext2D, scene: Scene): void {
  c.save();
  c.textAlign = "center";
  c.textBaseline = "middle";
  for (const p of scene.particles.all) {
    if (!p.live || p.kind !== "score" || !p.text) continue;
    const fade = 1 - p.age / p.life;
    const s = worldToScreen(scene.cam, scene.view, p.x, p.y);
    c.globalAlpha = Math.min(1, fade * 1.6);
    c.font = `700 ${Math.round(22 + fade * 8)}px Caveat, Nunito, ui-sans-serif, sans-serif`;
    c.lineWidth = 4;
    c.strokeStyle = colours.ink;
    c.strokeText(p.text, s.x, s.y);
    c.fillStyle = mono ? colours.cream : p.colour;
    c.fillText(p.text, s.x, s.y);
  }
  c.restore();
  c.globalAlpha = 1;
}

/**
 * Arrows at the edge for whatever is off screen. A plan view is only fair if
 * it tells you where the thing shooting at you is.
 */
function edgeMarkers(c: CanvasRenderingContext2D, scene: Scene): void {
  const { view } = scene;
  const pad = 26;
  for (const m of scene.markers) {
    const s = worldToScreen(scene.cam, scene.view, m.x, m.y);
    const inside = s.x > pad && s.x < view.width - pad && s.y > pad && s.y < view.height - pad;
    if (inside) continue;
    const cx = view.width / 2;
    const cy = view.height / 2;
    const dx = s.x - cx;
    const dy = s.y - cy;
    const angle = Math.atan2(dy, dx);
    const rx = view.width / 2 - pad;
    const ry = view.height / 2 - pad;
    const k = Math.min(Math.abs(rx / (dx || 0.0001)), Math.abs(ry / (dy || 0.0001)));
    const px = cx + dx * k;
    const py = cy + dy * k;

    c.save();
    c.translate(px, py);
    c.rotate(angle);
    c.beginPath();
    c.moveTo(9, 0);
    c.lineTo(-6, -7);
    c.lineTo(-6, 7);
    c.closePath();
    c.fillStyle = mono ? colours.cream : m.colour;
    c.fill();
    c.lineWidth = 2;
    c.strokeStyle = colours.ink;
    c.stroke();
    c.restore();
  }
}

// ---- a tank on its own, for a card ------------------------------------------

/**
 * Draw one tank, nose to the right, filling a small canvas. For the picker
 * on the title screen: the same drawing the game uses, so what you choose is
 * what you get. `size` is the hull against Standard, as in the garage.
 */
export function paintTankPreview(
  canvas: HTMLCanvasElement,
  opts: { colour: string; size: number; style: string; decal?: string; locked?: boolean; lit?: boolean },
): void {
  const c = canvas.getContext("2d");
  if (!c) return;
  const dpr = Math.min(2, globalThis.devicePixelRatio || 1);
  const w = canvas.clientWidth || 120;
  const h = canvas.clientHeight || 72;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  c.clearRect(0, 0, w, h);
  // A hull is 2.1 m long at size 1 with a barrel past it; fit that in.
  const scale = Math.min(w / 4.8, h / 2.8);
  c.translate(w / 2 - scale * 0.35, h / 2);
  c.scale(scale, scale);
  c.rotate(-0.16);
  c.lineJoin = "round";
  c.lineCap = "round";
  bucket = 0;
  const wasMono = mono;
  mono = !!opts.locked;
  const e: Entity = {
    kind: "player",
    hp: 1,
    maxHp: 1,
    fill: opts.colour,
    stroke: colours.ink,
    lw: 3,
    hw: 1.05 * opts.size,
    hh: 0.72 * opts.size,
    turret: 0.3,
    roll: 0.4,
    style: opts.style,
    throttle: opts.lit ? 1 : 0,
    paint: { colour: opts.colour, decal: opts.decal ?? "none" },
  };
  c.globalAlpha = opts.locked ? 0.45 : 1;
  tank(c, e, 3 / scale, 0, 5, {
    world: null as unknown as World,
    cam: { x: 0, y: 0, scale, rot: 0, shakeX: 0, shakeY: 0 },
    particles: null as unknown as Particles,
    view: { width: w, height: h },
    time: 0,
    markers: [],
    reduced: true,
    mono: !!opts.locked,
    roads: [],
    aim: null,
  });
  mono = wasMono;
}
